"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { s } from "./style.js";
import {
  PAGE_TITLES,
  sprintKicker,
  overviewKicker,
  applyStatus,
  applyHealth,
  prs,
  deploys,
} from "./data.js";
import Rail from "./components/Rail";
import HomePage from "./views/HomePage";
import SprintPage from "./views/SprintPage";
import ServerPage from "./views/ServerPage";
import ReviewPage from "./views/ReviewPage";
import ConfigPage from "./views/ConfigPage";
import EstimatePage from "./views/EstimatePage";
import TaskSheet from "./components/TaskSheet";
import NewTicketSheet from "./components/NewTicketSheet";
import ToastContainer from "./components/ToastContainer";
import LoginPage from "./views/LoginPage";
import useToast from "./hooks/useToast";
import { getSession, login, logout, fetchTasks as apiFetchTasks, fetchTaskSummary, updateTask as apiUpdateTask, createTask as apiCreateTask, connectSSE, fetchPlatforms, connectPlatformSSE } from "./api";
import type { Page, Role, Task, TaskStatus, User, Platform } from "./types";

// Props from the original data-props block.
const CONFIG = {
  startPage: "home" as Page, // 'home' | 'sprint' | 'server' | 'review'
  darkMode: false,
  railLabels: false,
  pulseAlerts: true,
};

const pad = (n: number) => String(n).padStart(2, "0");
const nowClock = (): string => {
  const d = new Date();
  return pad(d.getHours()) + ":" + pad(d.getMinutes());
};

export default function App() {
  const [page, setPage] = useState<Page>(CONFIG.startPage);
  const [clock, setClock] = useState(nowClock);
  const [scale, setScale] = useState(1);
  const [boxH, setBoxH] = useState(1024);
  const [dark, setDark] = useState<boolean | null>(null); // null => follow CONFIG.darkMode
  const [auth, setAuth] = useState<Role | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [summaryTasks, setSummaryTasks] = useState<Task[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [platformsLoading, setPlatformsLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [creating, setCreating] = useState(false);
  const [recentlyUpdated, setRecentlyUpdated] = useState<Set<string>>(new Set());
  const { toasts, showToast, dismissToast } = useToast();
  const selfUpdatesRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    getSession().then((session) => {
      if (session) {
        setAuth(session.role);
        setUser(session.user);
      }
    }).finally(() => setLoading(false));
  }, []);

  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      setTasks((await apiFetchTasks()).map(applyStatus));
      return true;
    } catch {
      setTasks([]);
      return false;
    } finally {
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!auth || page !== "sprint") {
      setTasksLoading(false);
      return;
    }
    fetchTasks();
  }, [auth, fetchTasks, page]);

  useEffect(() => {
    if (!auth) return;
    const source = connectSSE(async (updates) => {
      const relevant = (updates as { id: string; updatedAt: string }[]).filter(
        (u) => selfUpdatesRef.current.get(u.id) !== u.updatedAt
      );
      if (relevant.length) {
        const ids = new Set(relevant.map((u) => u.id));
        setRecentlyUpdated((prev) => new Set([...prev, ...ids]));
        await fetchTasks();
      }
    });
    return () => source.close();
  }, [auth, fetchTasks]);

  useEffect(() => {
    if (!auth) {
      setSummaryLoading(false);
      return;
    }
    setSummaryLoading(true);
    fetchTaskSummary()
      .then((data) => setSummaryTasks(data.map(applyStatus)))
      .catch(() => setSummaryTasks([]))
      .finally(() => setSummaryLoading(false));
  }, [auth]);

  useEffect(() => {
    if (!auth) {
      setPlatformsLoading(false);
      return;
    }
    setPlatformsLoading(true);
    fetchPlatforms()
      .then((data) => setPlatforms(data.map(applyHealth)))
      .catch(() => setPlatforms([]))
      .finally(() => setPlatformsLoading(false));
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    const source = connectPlatformSSE((data) => {
      setPlatforms((prev) => {
        const next = [...prev];
        for (const update of Array.isArray(data) ? data : [data]) {
          const index = next.findIndex((p) => p.id === (update as Platform).id);
          if (index !== -1) next[index] = applyHealth({ ...next[index], ...(update as Platform) });
        }
        return next;
      });
    });
    return () => source.close();
  }, [auth]);

  const isDark = dark === null ? CONFIG.darkMode : dark;

  // Apply theme to <html>, matching the original applyTheme().
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = isDark ? "dark" : "light";
  }, [isDark]);

  // Clock tick — every 15s.
  useEffect(() => {
    const id = setInterval(() => setClock(nowClock()), 15000);
    return () => clearInterval(id);
  }, []);

  // Fit: fill the width; the design box height follows the viewport.
  const scaleRef = useRef(scale);
  const boxHRef = useRef(boxH);
  scaleRef.current = scale;
  boxHRef.current = boxH;

  const fit = useCallback(() => {
    const w = window.innerWidth || document.documentElement.clientWidth;
    const h = window.innerHeight || document.documentElement.clientHeight;
    if (!(w > 0 && h > 0)) return;
    const raw = Math.max(0.35, Math.min(w / 1366, 2));
    const rounded = Math.round(raw * 1000) / 1000;
    const nextBoxH = Math.round(Math.max(700, h / rounded));
    if (rounded !== scaleRef.current) setScale(rounded);
    if (nextBoxH !== boxHRef.current) setBoxH(nextBoxH);
  }, []);

  useEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("load", fit);
    const raf = requestAnimationFrame(fit);
    let ro: ResizeObserver | undefined;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => fit());
      ro.observe(document.documentElement);
    }
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("load", fit);
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, [fit]);

  const pageTitles = { ...PAGE_TITLES, config: ["Configuration", "master data"] } as Record<Page, [string, string]>;
  const [title, baseKicker] = pageTitles[page] || ["", ""];
  const kicker =
      page === "home"
      ? overviewKicker(platforms, summaryTasks, prs)
      : page === "sprint"
        ? sprintKicker(tasks)
        : baseKicker;
  const pulseAnim = CONFIG.pulseAlerts
    ? "alertPulse 1.8s steps(1,end) infinite"
    : "none";

  const openTask = (t: Task) => {
    setSelectedTask(t);
    setRecentlyUpdated((prev) => {
      const next = new Set(prev);
      next.delete(t.id);
      return next;
    });
  };
  const closeTask = () => setSelectedTask(null);
  const handleLogin = async ({ mode, password, username }: { mode: Role; password: string; username?: string }) => {
    try {
      const session = await login(mode, password, username);
      setAuth(session.role);
      setUser(session.user);
      showToast("Logged in successfully", "success");
    } catch (error) {
      throw error;
    }
  };
  const handleLogout = async () => {
    await logout();
    setAuth(null);
    setUser(null);
    setPage(CONFIG.startPage);
  };
  const updateTask = async (id: string, updates: { status?: TaskStatus; repos?: string[]; ownerId?: string | null }) => {
    try {
      const updated = applyStatus(await apiUpdateTask(id, updates));
      selfUpdatesRef.current.set(updated.id, updated.updatedAt);
      setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)));
      setSummaryTasks((prev) =>
        updated.status === "On hold" || updated.status === "In progress"
          ? prev.some((t) => t.id === id)
            ? prev.map((t) => (t.id === id ? { ...t, ...updated } : t))
            : [...prev, updated]
          : prev.filter((t) => t.id !== id)
      );
      setSelectedTask((prev) => (prev && prev.id === id ? updated : prev));
      showToast("Task updated", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Task update failed", "error");
      throw error;
    }
  };
  const createTask = async (input: unknown) => {
    try {
      const created = applyStatus(await apiCreateTask(input));
      selfUpdatesRef.current.set(created.id, created.updatedAt);
      setTasks((prev) => [...prev, created]);
      setCreating(false);
      showToast("Task created", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Task creation failed", "error");
      throw error;
    }
  };

  if (loading) {
    return (
      <div style={s("display:flex; align-items:center; justify-content:center; height:100vh; width:100vw; background:var(--color-bg)")}>
        <Loader2 size={32} color="var(--color-text)" className="animate-spin" />
      </div>
    );
  }

  if (!auth) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div
      style={s(
        "width:100vw; height:100vh; overflow:hidden; background:var(--color-bg); display:flex; align-items:flex-start; justify-content:flex-start"
      )}
    >
      <div
        style={{
          ...s(
            "width:1366px; flex:none; position:relative; transform-origin:top left; display:flex; background:var(--color-bg); color:var(--color-text); font-family:var(--font-body); overflow:hidden"
          ),
          height: boxH + "px",
          transform: "scale(" + scale + ")",
        }}
      >
        <Rail
          page={page}
          railLabels={CONFIG.railLabels}
          isDark={isDark}
          auth={auth}
          onNavigate={setPage}
          onToggleTheme={() => setDark(!isDark)}
          onLogout={handleLogout}
        />

        <main style={s("flex:1; min-width:0; display:flex; flex-direction:column; overflow:hidden")}>
          <header
            style={s(
              "flex:none; display:flex; align-items:flex-end; justify-content:space-between; padding:22px 32px 18px; border-bottom:2px solid var(--color-divider)"
            )}
          >
            <div style={s("display:flex; align-items:baseline; gap:20px")}>
              <h1 style={s("margin:0; font-size:40px; letter-spacing:-0.02em")}>{title}</h1>
              <span
                style={s(
                  "font-size:16px; letter-spacing:0.12em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600)"
                )}
              >
                {kicker}
              </span>
            </div>
            <div style={s("display:flex; align-items:baseline; gap:24px")}>
              <span
                style={s(
                  "font-size:16px; letter-spacing:0.12em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800"
                )}
              >
                Mon 01 Sep
              </span>
              <span
                style={s(
                  "font-size:32px; font-family:var(--font-heading); font-weight:800; font-variant-numeric:tabular-nums; letter-spacing:-0.02em"
                )}
              >
                {clock}
              </span>
            </div>
          </header>

          {page === "home" && (
            <HomePage
              platforms={platforms}
              tasks={summaryTasks}
              prs={prs}
              deploys={deploys}
              tasksLoading={summaryLoading}
              pulseAnim={pulseAnim}
              onNavigate={setPage}
              onOpenTask={openTask}
            />
          )}
          {page === "sprint" && (
            <SprintPage
              tasks={tasks}
              tasksLoading={tasksLoading}
              auth={auth}
              onOpenTask={openTask}
              onCreate={() => setCreating(true)}
              onUpdate={updateTask}
              recentlyUpdated={recentlyUpdated}
            />
          )}
          {page === "server" && <ServerPage platforms={platforms} platformsLoading={platformsLoading} pulseAnim={pulseAnim} />}
          {page === "review" && <ReviewPage pulseAnim={pulseAnim} />}
          {page === "config" && <ConfigPage onToast={showToast} />}
          {page === "estimate" && <EstimatePage onNavigate={setPage} onToast={showToast} />}
        </main>

        {selectedTask && (
          <TaskSheet
            task={selectedTask}
            onClose={closeTask}
            onUpdate={updateTask}
            auth={auth}
            user={user}
            onToast={showToast}
          />
        )}

        {creating && (
          <NewTicketSheet
            onClose={() => setCreating(false)}
            onCreate={createTask}
            defaultOwnerId={user?.id}
          />
        )}

        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </div>
    </div>
  );
}
